import { useEffect } from "react";

/**
 * Chống ngủ màn hình trong lúc học — Prompt.md §24.
 *
 * `navigator.wakeLock` **không** có trên iOS Safari và trên Firefox: toàn bộ hook này là
 * best-effort trong try/catch, không có fallback kiểu phát video ẩn (tốn pin, và §7 không cho
 * dùng media để lách). Thiếu nó thì màn hình tắt sau ~30 giây và camera dừng — nên đây là tính
 * năng "có thì tốt hơn nhiều", không phải điều kiện để phiên học chạy.
 *
 * Xin lại lock khi tab quay lại foreground là bắt buộc: trình duyệt **tự thu hồi** sentinel khi
 * tab bị ẩn, và không xin lại thì lock chỉ sống tới lần đầu trẻ mở app khác.
 */
export function useWakeLock(active: boolean): void {
  useEffect(() => {
    if (!active) return;

    const wakeLock = navigator.wakeLock;
    if (wakeLock === undefined) return;

    let released = false;
    let sentinel: WakeLockSentinel | null = null;

    const request = (): void => {
      if (released || sentinel !== null || document.visibilityState !== "visible") return;
      wakeLock
        .request("screen")
        .then((next) => {
          if (released) {
            void next.release().catch(() => undefined);
            return;
          }
          sentinel = next;
          next.addEventListener("release", () => {
            sentinel = null;
          });
        })
        .catch(() => undefined);
    };

    const onVisibility = (): void => request();

    request();
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      released = true;
      document.removeEventListener("visibilitychange", onVisibility);
      void sentinel?.release().catch(() => undefined);
    };
  }, [active]);
}
